import type {
  ColumnInfo,
  FacetResultData,
  QueryVectorIndexRequest,
  QueryVectorIndexResponse,
  RerankerConfig,
  VectorSearchClient,
} from "@databricks/sdk-vectorsearch/v1";
import type {
  EmbeddingProvider,
  JsonObject,
  RetrieveOptions,
  RetrievedChunk,
  Retriever,
} from "@fabric-harness/sdk";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

export type DatabricksVectorSearchClient = Pick<VectorSearchClient, "queryVectorIndex"> &
  Partial<Pick<VectorSearchClient, "queryVectorIndexNextPage">>;

export type DatabricksAiSearchInputMode = "text" | "vector" | "text-and-vector";
export type DatabricksAiSearchStrategy = "ann" | "hybrid" | "full-text";

export interface DatabricksAiSearchQueryOptions extends RetrieveOptions {
  /** Override the configured input mode for this query. */
  inputMode?: DatabricksAiSearchInputMode;
  /** Override the configured retrieval algorithm for this query. */
  strategy?: DatabricksAiSearchStrategy;
  /** Precomputed query vector. Required when no embeddings provider can supply a vector input. */
  queryVector?: number[];
  /** Minimum score accepted by Databricks AI Search. */
  scoreThreshold?: number;
  /** Text columns searched for the query text. */
  queryColumns?: string[];
  /** Sort clauses such as `rating DESC`. */
  sortColumns?: string[];
  /** Facet expressions returned with the result set. */
  facets?: string[];
  /** Native Databricks reranker configuration. */
  reranker?: RerankerConfig;
  /** Columns supplied to the native reranker. */
  columnsToRerank?: string[];
}

export interface DatabricksAiSearchQueryResult {
  chunks: RetrievedChunk[];
  nextPageToken?: string;
  facetResult?: FacetResultData;
  /** Complete generated-SDK response for callers that need native result metadata. */
  response: QueryVectorIndexResponse;
}

export interface DatabricksAiSearchRetriever extends Retriever {
  readonly kind: "databricks-ai-search";
  readonly index: string;
  readonly inputMode: DatabricksAiSearchInputMode;
  readonly strategy: DatabricksAiSearchStrategy;
  query(
    query: string,
    options?: DatabricksAiSearchQueryOptions,
  ): Promise<DatabricksAiSearchQueryResult>;
  nextPage(
    pageToken: string,
    options?: { signal?: AbortSignal },
  ): Promise<DatabricksAiSearchQueryResult>;
}

export interface DatabricksAiSearchOptions {
  client: DatabricksVectorSearchClient;
  /**
   * Private bundle transport used only when SDK 0.21 rejects a valid floating-point score while
   * decoding a live query response. Other SDK failures remain fail-closed.
   */
  rawProtocolClient?: DatabricksRawProtocolClient;
  /** Full index name: `catalog.schema.index`. */
  index: string;
  /** Column that holds the chunk text returned to the model. */
  textColumn: string;
  /** Columns to retrieve. Defaults to `[textColumn]` plus `idColumn` when set. */
  columns?: string[];
  /** Column to surface as `RetrievedChunk.id`. */
  idColumn?: string;
  /**
   * Query representation. Text indexes default to `text`; self-managed indexes use `vector`;
   * `text-and-vector` sends both inputs where the selected Databricks strategy supports them.
   */
  inputMode?: DatabricksAiSearchInputMode;
  /**
   * Databricks retrieval algorithm. Text input defaults to `hybrid`, while vector-only input
   * defaults to `ann`.
   */
  strategy?: DatabricksAiSearchStrategy;
  /** Required for vector input unless each query supplies `queryVector`. */
  embeddings?: EmbeddingProvider;
  scoreThreshold?: number;
  queryColumns?: string[];
  sortColumns?: string[];
  facets?: string[];
  reranker?: RerankerConfig;
  columnsToRerank?: string[];
  name?: string;
}

const SCORE_COLUMN = "score";

/** Databricks AI Search as a {@link Retriever}. Runs under the bundle's Unity Catalog principal. */
export function databricksAiSearch(
  options: DatabricksAiSearchOptions,
): DatabricksAiSearchRetriever {
  const configuredInputMode = options.inputMode ?? "text";
  const configuredStrategy =
    options.strategy ?? (configuredInputMode === "vector" ? "ann" : "hybrid");
  const columns =
    options.columns ??
    dedupe([options.textColumn, ...(options.idColumn ? [options.idColumn] : [])]);

  const retriever: DatabricksAiSearchRetriever = {
    kind: "databricks-ai-search",
    name: options.name ?? `databricks-ai-search:${options.index}`,
    index: options.index,
    inputMode: configuredInputMode,
    strategy: configuredStrategy,
    async retrieve(query: string, opts: RetrieveOptions = {}): Promise<RetrievedChunk[]> {
      return (await retriever.query(query, opts)).chunks;
    },
    async query(
      query: string,
      opts: DatabricksAiSearchQueryOptions = {},
    ): Promise<DatabricksAiSearchQueryResult> {
      if (!query.trim()) throw new Error("databricks AI Search: query must not be empty.");
      const inputMode = opts.inputMode ?? configuredInputMode;
      const strategy = opts.strategy ?? configuredStrategy;
      validateQueryCombination(inputMode, strategy);
      const scoreThreshold = opts.scoreThreshold ?? options.scoreThreshold;
      const queryColumns = opts.queryColumns ?? options.queryColumns;
      const sortColumns = opts.sortColumns ?? options.sortColumns;
      const facets = opts.facets ?? options.facets;
      const reranker = opts.reranker ?? options.reranker;
      const columnsToRerank = opts.columnsToRerank ?? options.columnsToRerank;
      const body: QueryVectorIndexRequest = {
        name: options.index,
        columns,
        numResults: opts.k ?? 5,
        queryType: nativeStrategy(strategy),
        ...(scoreThreshold !== undefined ? { scoreThreshold } : {}),
        ...(queryColumns ? { queryColumns } : {}),
        ...(sortColumns ? { sortColumns } : {}),
        ...(facets ? { facets } : {}),
        ...(reranker ? { reranker } : {}),
        ...(columnsToRerank ? { columnsToRerank } : {}),
      };
      if (opts.filter) body.filtersJson = JSON.stringify(opts.filter);
      if (inputMode !== "vector") {
        body.queryText = query;
      }
      if (inputMode !== "text") {
        body.queryVector = await resolveQueryVector(query, opts, options);
      }

      const response = await queryVectorIndex(options, body, opts.signal);
      return queryResult(response, options, columns);
    },
    async nextPage(
      pageToken: string,
      opts: { signal?: AbortSignal } = {},
    ): Promise<DatabricksAiSearchQueryResult> {
      if (!pageToken.trim()) throw new Error("databricks AI Search: page token must not be empty.");
      if (!options.client.queryVectorIndexNextPage) {
        throw new Error(
          "databricks AI Search: the configured client does not support result pagination.",
        );
      }
      const response = opts.signal
        ? await options.client.queryVectorIndexNextPage(
            { name: options.index, pageToken },
            { signal: opts.signal },
          )
        : await options.client.queryVectorIndexNextPage({ name: options.index, pageToken });
      return queryResult(response, options, columns);
    },
  };
  return retriever;
}

interface RawQueryVectorIndexResponse {
  manifest?: {
    columns?: Array<{ name?: string }>;
  };
  result?: {
    data_array?: unknown[][];
    row_count?: number;
  };
  next_page_token?: string;
  facet_result?: unknown;
}

async function queryVectorIndex(
  options: DatabricksAiSearchOptions,
  body: QueryVectorIndexRequest,
  signal?: AbortSignal,
): Promise<QueryVectorIndexResponse> {
  try {
    return signal
      ? await options.client.queryVectorIndex(body, { signal })
      : await options.client.queryVectorIndex(body);
  } catch (error) {
    if (!options.rawProtocolClient || !isGeneratedFloatingPointDecodeError(error)) throw error;
    const raw = await options.rawProtocolClient.post<RawQueryVectorIndexResponse>(
      `/api/2.0/vector-search/indexes/${encodeURIComponent(options.index)}/query`,
      {
        columns: body.columns,
        num_results: body.numResults,
        ...(body.filtersJson !== undefined ? { filters_json: body.filtersJson } : {}),
        ...(body.queryText !== undefined ? { query_text: body.queryText } : {}),
        ...(body.queryVector !== undefined ? { query_vector: body.queryVector } : {}),
        ...(body.queryType !== undefined ? { query_type: body.queryType } : {}),
        ...(body.scoreThreshold !== undefined ? { score_threshold: body.scoreThreshold } : {}),
        ...(body.queryColumns !== undefined ? { query_columns: body.queryColumns } : {}),
        ...(body.sortColumns !== undefined ? { sort_columns: body.sortColumns } : {}),
        ...(body.facets !== undefined ? { facets: body.facets } : {}),
        ...(body.reranker !== undefined ? { reranker: body.reranker } : {}),
        ...(body.columnsToRerank !== undefined ? { columns_to_rerank: body.columnsToRerank } : {}),
      },
      undefined,
      { retry: "safe", ...(signal ? { signal } : {}) },
    );
    return {
      ...(raw.manifest ? { manifest: raw.manifest } : {}),
      ...(raw.result
        ? {
            result: {
              ...(raw.result.data_array ? { dataArray: raw.result.data_array as never } : {}),
              ...(raw.result.row_count !== undefined ? { rowCount: raw.result.row_count } : {}),
            },
          }
        : {}),
      ...(raw.next_page_token ? { nextPageToken: raw.next_page_token } : {}),
      ...(raw.facet_result ? { facetResult: raw.facet_result as FacetResultData } : {}),
    };
  }
}

async function resolveQueryVector(
  query: string,
  queryOptions: DatabricksAiSearchQueryOptions,
  options: DatabricksAiSearchOptions,
): Promise<number[]> {
  if (queryOptions.queryVector) {
    if (queryOptions.queryVector.length === 0)
      throw new Error("databricks AI Search: queryVector must not be empty.");
    return queryOptions.queryVector;
  }
  if (!options.embeddings) {
    throw new Error(
      "databricks AI Search: vector input requires `queryVector` or an embeddings provider.",
    );
  }
  const [vector] = queryOptions.signal
    ? await options.embeddings.embed([query], { signal: queryOptions.signal })
    : await options.embeddings.embed([query]);
  if (!vector?.length)
    throw new Error("databricks AI Search: embeddings returned no vector for the query.");
  return vector;
}

function validateQueryCombination(
  inputMode: DatabricksAiSearchInputMode,
  strategy: DatabricksAiSearchStrategy,
): void {
  if ((strategy === "hybrid" || strategy === "full-text") && inputMode === "vector") {
    throw new Error(
      `databricks AI Search: ${strategy} retrieval requires text or text-and-vector input.`,
    );
  }
  if (strategy === "full-text" && inputMode === "text-and-vector") {
    throw new Error("databricks AI Search: full-text retrieval does not accept a query vector.");
  }
}

function nativeStrategy(strategy: DatabricksAiSearchStrategy): string {
  if (strategy === "full-text") return "FULL_TEXT";
  return strategy.toUpperCase();
}

function queryResult(
  response: QueryVectorIndexResponse,
  options: DatabricksAiSearchOptions,
  requestedColumns: string[],
): DatabricksAiSearchQueryResult {
  return {
    chunks: mapResults(response, options, requestedColumns),
    ...(response.nextPageToken ? { nextPageToken: response.nextPageToken } : {}),
    ...(response.facetResult ? { facetResult: response.facetResult } : {}),
    response,
  };
}

function isGeneratedFloatingPointDecodeError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return /Cannot convert [-+]?\d+\.\d+(?:e[-+]?\d+)? to a BigInt/iu.test(message);
}

function mapResults(
  response: QueryVectorIndexResponse,
  options: DatabricksAiSearchOptions,
  requestedColumns: string[],
): RetrievedChunk[] {
  const names = (response.manifest?.columns ?? []).map((c: ColumnInfo) => c.name ?? "");
  const requested = new Set(requestedColumns);
  // Identify the score column: a literal `score`, else a single trailing column the response
  // appended beyond what we requested (Databricks adds the similarity score). Never treat a
  // requested column as the score — that would mis-attribute a numeric metadata field.
  const lastName = names[names.length - 1];
  const scoreColumn = names.includes(SCORE_COLUMN)
    ? SCORE_COLUMN
    : lastName && !requested.has(lastName)
      ? lastName
      : undefined;

  const rows = response.result?.dataArray ?? [];
  return rows.map((row: unknown[]) => {
    const record: JsonObject = {};
    names.forEach((name: string, i: number) => {
      record[name] = row[i] as JsonObject[string];
    });

    const text = String(record[options.textColumn] ?? "");
    const scoreRaw = scoreColumn ? record[scoreColumn] : undefined;
    const score = typeof scoreRaw === "number" ? scoreRaw : undefined;
    const idRaw = options.idColumn ? record[options.idColumn] : undefined;

    const metadata: JsonObject = { ...record };
    delete metadata[options.textColumn];
    if (scoreColumn) delete metadata[scoreColumn];
    if (options.idColumn) delete metadata[options.idColumn];

    return {
      ...(idRaw != null ? { id: String(idRaw) } : {}),
      text,
      ...(score !== undefined ? { score } : {}),
      metadata,
    };
  });
}

function dedupe(values: string[]): string[] {
  return [...new Set(values)];
}
